import axios from 'axios';
import { API_BASE_URL, ORDER_GENIE_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

/**
 * API layer for the ExtremeB2B MCP integration UI.
 *
 * - Auth-key management hits the main backend (`/api/v1/auth/keys`).
 * - The MCP tool catalog and "playground" invocations hit the OrderX
 *   service (`/api/v1/order-genie/...`), exactly like the MCP server does.
 */

function buildClient(baseURL) {
  const instance = axios.create({ baseURL, withCredentials: true });
  instance.interceptors.request.use((config) => {
    // getAuthHeadersFromStorage() already prioritizes OAuth over API key and
    // returns the right single credential — don't also attach a (possibly
    // stale) X-API-Key on top, or an OAuth request can carry a leftover key
    // the backend rejects even though the Bearer token is valid.
    const authHeaders = getAuthHeadersFromStorage();
    if (Object.keys(authHeaders).length) {
      config.headers = { ...authHeaders, ...config.headers };
    }
    return config;
  });
  attachAuthInterceptor(instance);
  return instance;
}

const authClient = buildClient(API_BASE_URL);
const orderGenieClient = buildClient(ORDER_GENIE_API_BASE_URL);

function extractError(error) {
  const payload = error?.response?.data;
  const detail = payload?.detail ?? payload?.message;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join(', ');
  }
  if (detail) return String(detail);
  if (error?.code === 'ECONNABORTED') return 'Request timed out.';
  return error?.message || 'Request failed';
}

async function run(promise) {
  try {
    const { data } = await promise;
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
}

/* ----- Auth keys (main backend) ----- */

export const listAuthKeys = () => run(authClient.get('/api/v1/auth/keys'));

export const createAuthKey = (payload) => run(authClient.post('/api/v1/auth/keys', payload));

export const updateAuthKey = (keyId, payload) =>
  run(authClient.put(`/api/v1/auth/keys/${keyId}`, payload));

export const rotateAuthKey = (keyId) =>
  run(authClient.post(`/api/v1/auth/keys/${keyId}/rotate`));

export const deactivateAuthKey = (keyId) =>
  run(authClient.delete(`/api/v1/auth/keys/${keyId}`));

export const getAuthKeyUsage = () => run(authClient.get('/api/v1/auth/keys/usage'));

/* ----- MCP catalog + playground (OrderX service) ----- */

export const getMcpTools = () =>
  run(orderGenieClient.get('/api/v1/orderx/mcp/tools'));

/**
 * Replay an MCP tool against its underlying OrderX REST endpoint.
 *
 * @param {object} tool   Catalog entry (with `http` + `params`).
 * @param {object} values Map of param name -> value supplied by the user.
 */
export const invokeMcpTool = (tool, values) => {
  const method = String(tool?.http?.method || 'GET').toLowerCase();
  let path = String(tool?.http?.path || '');
  const params = {};
  const body = {};

  (tool?.params || []).forEach((param) => {
    const raw = values?.[param.name];
    if (raw === undefined || raw === null || raw === '') return;
    const value = coerce(raw, param.type);
    if (param.in === 'path') {
      path = path.replace(`{${param.name}}`, encodeURIComponent(String(value)));
    } else if (param.in === 'query') {
      params[param.name] = value;
    } else if (param.in === 'body') {
      body[param.name] = value;
    }
  });

  const url = `/api/v1/orderx${path}`;
  if (method === 'get') return run(orderGenieClient.get(url, { params }));
  if (method === 'delete') return run(orderGenieClient.delete(url, { params }));
  if (method === 'patch') return run(orderGenieClient.patch(url, body, { params }));
  if (method === 'put') return run(orderGenieClient.put(url, body, { params }));
  return run(orderGenieClient.post(url, body, { params }));
};

function coerce(value, type) {
  if (type === 'integer') {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? value : n;
  }
  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    return ['true', '1', 'yes', 'on'].includes(String(value).toLowerCase());
  }
  if (type === 'array') {
    if (Array.isArray(value)) return value;
    return String(value)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return value;
}
